import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  assignRotorWingShifts,
  changeRotorWingShifts,
  getDailyLineAccess,
  getRotorWingCalendar,
  listRotorWingEligibleCrew,
  listRotorWingShifts,
  updateRotorWingShiftStatus,
} from '../../../api/scheduler/functions';
import { AssignRotorWingShiftsRequest } from '../../../api/scheduler/models/assign-rotor-wing-shifts-request';
import { ChangeRotorWingShiftsRequest } from '../../../api/scheduler/models/change-rotor-wing-shifts-request';
import { RotorWingCalendarDto } from '../../../api/scheduler/models/rotor-wing-calendar-dto';
import { RotorWingShift } from '../../../api/scheduler/models/rotor-wing-shift';
import { SchedulerApiConfiguration } from '../../../api/scheduler/scheduler-api-configuration';
import {
  DailyLineCalendarDto,
  DailyLineCrewDto,
  DailyLineDto,
  DailyLineEnvelope,
  DailyLineScopedBaseDto,
  DailyLineShiftDto,
  EligibleCrewDto,
} from '../models/daily-line-schedule.models';

@Injectable({ providedIn: 'root' })
export class DailyLineScheduleApiService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(SchedulerApiConfiguration);

  private url(path: string): string {
    return `${this.config.rootUrl.replace(/\/$/, '')}${path}`;
  }

  getAccess(): Observable<DailyLineScopedBaseDto[]> {
    // The access endpoint returns Active and Inactive Bases unless `status` is given.
    return getDailyLineAccess(this.http, this.config.rootUrl, { status: 'ACTIVE' }).pipe(
      map((res) =>
        (res.body.data?.rotorWing.bases ?? []).map((base) => ({
          code: base.code,
          name: base.name,
        }))
      )
    );
  }

  getCalendar(dateFrom: string, dateTo: string, base?: string): Observable<DailyLineCalendarDto> {
    return getRotorWingCalendar(this.http, this.config.rootUrl, { dateFrom, dateTo, base }).pipe(
      map((res) => mapRotorCalendar(res.body.data))
    );
  }

  listLines(activeOnly = false): Observable<DailyLineDto[]> {
    return this.http
      .get<DailyLineEnvelope<DailyLineDto[]>>(
        this.url('/api/v1/scheduler/daily-line-schedule/lines'),
        {
          params: { activeOnly: String(activeOnly) },
        }
      )
      .pipe(map((res) => res.data ?? []));
  }

  createLine(name: string): Observable<DailyLineDto> {
    return this.http
      .post<DailyLineEnvelope<DailyLineDto>>(
        this.url('/api/v1/scheduler/daily-line-schedule/lines'),
        {
          name,
        }
      )
      .pipe(map((res) => res.data));
  }

  setLineStatus(id: number, active: boolean): Observable<DailyLineDto> {
    return this.http
      .patch<DailyLineEnvelope<DailyLineDto>>(
        this.url(`/api/v1/scheduler/daily-line-schedule/lines/${id}/status`),
        { active }
      )
      .pipe(map((res) => res.data));
  }

  listShifts(base: string): Observable<DailyLineShiftDto[]> {
    return listRotorWingShifts(this.http, this.config.rootUrl, { base }).pipe(
      map((res) => (res.body.data?.shifts ?? []).map(mapShiftStatus))
    );
  }

  setShiftStatus(
    base: string,
    shift: RotorWingShift,
    active: boolean
  ): Observable<DailyLineShiftDto> {
    return updateRotorWingShiftStatus(this.http, this.config.rootUrl, {
      body: { base, shift, active },
    }).pipe(
      map((res) => {
        const match = (res.body.data?.shifts ?? []).find((item) => item.shift === shift);
        return mapShiftStatus(match ?? { shift, active });
      })
    );
  }

  listCrew(): Observable<DailyLineCrewDto[]> {
    return this.http
      .get<DailyLineEnvelope<DailyLineCrewDto[]>>(
        this.url('/api/v1/scheduler/daily-line-schedule/crew')
      )
      .pipe(map((res) => res.data ?? []));
  }

  assignShifts(body: AssignRotorWingShiftsRequest): Observable<DailyLineCalendarDto> {
    return assignRotorWingShifts(this.http, this.config.rootUrl, { body }).pipe(
      map((res) => mapRotorCalendar(res.body.data?.calendar))
    );
  }

  changeShifts(body: ChangeRotorWingShiftsRequest): Observable<DailyLineCalendarDto> {
    return changeRotorWingShifts(this.http, this.config.rootUrl, { body }).pipe(
      map((res) => mapRotorCalendar(res.body.data?.calendar))
    );
  }

  listEligibleCrew(base: string, dateFrom: string, dateTo: string): Observable<EligibleCrewDto[]> {
    return listRotorWingEligibleCrew(this.http, this.config.rootUrl, {
      base,
      dateFrom,
      dateTo,
    }).pipe(
      map((res) =>
        (res.body.data ?? []).map((member) => ({
          crewId: member.crewId,
          crewName: member.crewName,
          rank: member.rank,
          baseCode: base,
          baseName: base,
        }))
      )
    );
  }
}

const SHIFT_LABELS: Record<RotorWingShift, string> = {
  DAY: 'Day',
  NIGHT: 'Night',
};

/** Base-specific shifts have a hex code, so their label comes from the API `name`. */
function mapShiftStatus(shift: {
  shift: RotorWingShift;
  active: boolean;
  name?: string;
}): DailyLineShiftDto {
  return {
    code: shift.shift,
    label: shift.name?.trim() || SHIFT_LABELS[shift.shift] || shift.shift,
    active: shift.active,
  };
}

function mapRotorCalendar(calendar: RotorWingCalendarDto | undefined): DailyLineCalendarDto {
  return {
    wingType: 'ROTOR_WING',
    dateFrom: calendar?.dateFrom ?? '',
    dateTo: calendar?.dateTo ?? '',
    rotorBaseGroups: (calendar?.bases ?? []).map((group) => ({
      baseCode: group.base,
      baseName: group.baseName,
      baseStatus: group.baseStatus ?? 'ACTIVE',
      crew: group.crew.map((row) => ({
        crewId: row.crewId,
        crewName: row.crewName,
        rank: row.rank ?? null,
        available: row.available,
        cells: row.cells.map((cell) => ({
          date: cell.date,
          shift: cell.shift ?? null,
          masterScheduleAllocated: cell.masterScheduleAllocated,
        })),
      })),
    })),
  };
}
